import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RADIO_PLAN_DESC } from '@app/core/models/Radio';
import { LiveTrackingState } from '@app/core/states/live-tracking.state';
import { UploadFileList } from '@app/core/states/upload.actions';
import { Playlist } from '@app/library/models/playlist.model';
import { BacsState } from '@app/library/states/bacs.state';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { SugestionInput } from '@radioking/ui/input';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

interface DataInput {
  files: FileList;
  playlist: Playlist;
}

@Component({
  selector: 'rk-playlist-upload-box-modal',
  templateUrl: './playlist-upload-box-modal.component.html',
  styleUrls: ['./playlist-upload-box-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaylistUploadBoxModalComponent implements OnInit, OnDestroy {
  selectBoxForm: UntypedFormGroup;
  accept = '.mp3';

  @Select(BacsState.allUnlockedBoxesForSelect)
  bacs$: Observable<SugestionInput[]>;

  @Select(LiveTrackingState.planDesc)
  planDesc$: Observable<RADIO_PLAN_DESC>;

  $boxes: WritableSignal<SugestionInput[]> = signal(null);

  @ViewChild('trackUploadFileInput', { static: true })
  trackUploadFileInput: ElementRef;

  isLoading$: Observable<boolean>;

  subscription = new Subscription();

  constructor(
    public readonly dialogRef: MatDialogRef<PlaylistUploadBoxModalComponent>,
    private readonly actions$: Actions,
    private readonly store: Store,
    @Inject(MAT_DIALOG_DATA) public dataIn: DataInput,
  ) {}

  ngOnInit() {
    const datas$ = this.bacs$.pipe(filter(data => !!data));
    this.isLoading$ = datas$.pipe(
      map(() => false),
      startWith(true),
    );
    this.subscription.add(datas$.subscribe(data => this.createForm(data)));
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(UploadFileList))
        .subscribe(() => this.dialogRef.close()),
    );
    this.subscription.add(
      combineLatest([this.bacs$, this.planDesc$]).subscribe(([boxes, plan]) => {
        this.$boxes.set(
          ['Discover', 'Demo'].indexOf(plan) > -1
            ? boxes.filter(b => b.name !== '__DEDICATION__')
            : boxes,
        );
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private createForm(bacs: SugestionInput[]) {
    this.selectBoxForm = new UntypedFormBuilder().group({
      targetBac: bacs[0],
    });
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onSubmit() {
    if (this.dataIn.files) {
      this.uploadFiles(this.dataIn.files);
    } else {
      if (this.trackUploadFileInput) {
        this.trackUploadFileInput.nativeElement.click();
      }
    }
  }

  async onFileSelect(event: any) {
    this.uploadFiles(event.target.files);
    this.trackUploadFileInput.nativeElement.value = '';
  }

  @Dispatch()
  uploadFiles(files: FileList) {
    const box = this.store.selectSnapshot(BacsState.rootBacById)(
      this.selectBoxForm.value.targetBac.id,
    );

    return new UploadFileList(files, box, this.dataIn.playlist);
  }
}
